
import {
    HttpClient,
    HttpHeaders,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { HttpService } from './http.service';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { API_URL_PREFIX } from '../constants';
import { IBuildingIntegration } from '../interfaces/i-building-integration';
import { IntegrationType } from '../enums/integration-type.enum';
import { IBuildingIntegrationConfigFile } from '../interfaces/i-building-integration-configfile';

@Injectable({
    providedIn: 'root',
})
export class BuildingIntegrationService extends HttpService {
    public baseUrl = '';

    public state: {
        buildingIntegrations$: BehaviorSubject<IBuildingIntegration[]>;
    } = {
        buildingIntegrations$: new BehaviorSubject<IBuildingIntegration[]>([]),
    };

    constructor(private httpClient: HttpClient, public injector: Injector) {
        super(httpClient);
        this.baseUrl = `${environment.portal_api_endpoint}${API_URL_PREFIX}`;
    }
    /**
     * Fetch integrations for a specific building by its ID.
     * @param projectId - The ID of the building.
     * @returns Observable containing the integrations data.
     */
    public getBuildingIntegrations(
        projectId: string,
        accessToken: string
    ): Observable<IBuildingIntegration[]> {
        const url = `${environment.gateway_endpoint}/api/v1/buildings/${projectId}/integrations`;
        const headers = new HttpHeaders({
            Authorization: `Bearer ${accessToken}`,
        });
        return this.get<any[]>(url, null, headers).pipe(
            tap((res: IBuildingIntegration[]) =>
                this.state.buildingIntegrations$.next(res)
            )
        );
    }



    /**
     * Fetch Project JSON data of the project by its ID.
     * @param projectId - The ID of the project.
     * @returns Observable containing the project JSON data.
     */
    public fetchProjectJsonData(projectId: string): Observable<any> {
        const url = `${this.baseUrl}networks/v2/download/${projectId}`;
        return this.get<any[]>(url);
    }

    /**
     * Fetch the integration configuration file for a specific building and integration type.
     * @param projectId - The ID of the project.
     * @param integrationType - The type of integration.
     * @returns Observable containing the integration configuration file.
     */
    public fetchIntegrationConfigurationFile(projectId: string, integrationType: IntegrationType, accessToken: string): Observable<IBuildingIntegrationConfigFile> {
        const url = `${environment.gateway_endpoint}/api/v1/buildings/${projectId}/integrations/${integrationType}/configuration`;
        const headers = new HttpHeaders({
            Authorization: `Bearer ${accessToken}`,
        });
        return this.get<IBuildingIntegrationConfigFile>(url, null, headers).pipe(
            tap((res: IBuildingIntegrationConfigFile) => {
                // Handle the response if needed
                this.updateObservable(this.state.buildingIntegrations$, (l: IBuildingIntegration[]) => {
                    const integrationIndex = l.findIndex((obj) => obj.integrationType === IntegrationType.BACNET);
                    l[integrationIndex] ={ ...l[integrationIndex],
                        configGenerationRequired: res.configGenerationRequired,
                        configGeneratedOnUtc: res.configGeneratedOnUtc
                    };
                    return l;
                });
            })
        );
    }

    /**
     * Enable the BACnet integration for a specific building.
     * @param projectId - The ID of the project/building.
     * @returns Observable containing the response JSON.
     */
    public enableBACnetIntegration(buildingId: string, accessToken: string): Observable<void> {
        const url = `${environment.gateway_endpoint}/api/v1/buildings/${buildingId}/integrations`;
        const headers = new HttpHeaders({
            Authorization: `Bearer ${accessToken}`,
        });
        const requestBody = {
            integrationType: IntegrationType.BACNET
        };
        return this.post<void>(url, requestBody, headers);
    }

}